import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { desktopRecordingBridge, type DesktopReminderInfo } from '@/lib/desktop-recording';
import { useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/meeting-audio-status';
import { useWorkspace } from './context';
import { requestMeetingRecording } from './meeting-record-intent';
import { WorkspaceIcon } from './icons';

export function MeetingReminderPreference() {
  const copy = useNamespaceCopy(copyCatalog);
  const [info, setInfo] = useState<DesktopReminderInfo | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    void desktopRecordingBridge()
      ?.getInfo()
      .then((value) => {
        if (active) setInfo(value.meetingReminders ?? null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  if (!info) return null;
  const toggle = async (enabled: boolean) => {
    const bridge = desktopRecordingBridge();
    if (!bridge?.setMeetingRemindersEnabled) return;
    setBusy(true);
    try {
      setInfo(await bridge.setMeetingRemindersEnabled(enabled));
    } catch {
      setInfo((current) => (current ? { ...current, capability: 'unavailable' } : current));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ws-meeting-reminder-preference">
      <label>
        <input
          type="checkbox"
          checked={info.enabled}
          disabled={busy}
          onChange={(event) => void toggle(event.target.checked)}
        />{' '}
        {copy.reminders}
      </label>
      <p className="ws-page-note">{copy.remindersHint}</p>
      {info.enabled && info.capability === 'unavailable' && (
        <p className="ws-page-note">{copy.unavailable}</p>
      )}
    </div>
  );
}

/** Mounted only while this signed-in workspace has no active recording. */
export function MeetingReminder() {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(copyCatalog);
  const [pending, setPending] = useState(false);
  const previousFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const bridge = desktopRecordingBridge();
    if (!bridge?.onMeetingReminder) return;
    let active = true;
    const unsubscribe = bridge.onMeetingReminder(() => setPending(true));
    void bridge
      .getInfo()
      .then((value) => {
        if (active && value.meetingReminders?.pending) setPending(true);
      })
      .catch(() => {});
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);
  const dismiss = () => {
    setPending(false);
    void desktopRecordingBridge()
      ?.dismissMeetingReminder?.()
      .catch(() => {});
  };
  return (
    <Dialog open={pending} onOpenChange={(open) => !open && dismiss()}>
      <DialogContent
        aria-modal="true"
        className="ws-portal ws-meeting-reminder-dialog max-h-[calc(100dvh-2rem)] gap-6 overflow-y-auto p-8"
        onOpenAutoFocus={() => {
          previousFocus.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (previousFocus.current?.isConnected) previousFocus.current.focus();
          previousFocus.current = null;
        }}
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="bg-muted flex size-14 items-center justify-center rounded-2xl">
            <WorkspaceIcon name="meetings" size={28} />
          </span>
          <DialogTitle className="text-2xl leading-tight">{copy.detected}</DialogTitle>
          <DialogDescription className="max-w-sm leading-relaxed">
            {copy.remindersHint}
          </DialogDescription>
        </div>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <button className="ws-button justify-center" onClick={dismiss}>
              {copy.dismiss}
            </button>
            <button
              className="ws-button primary justify-center"
              onClick={() => {
                previousFocus.current = null;
                requestMeetingRecording();
                ws.navigate('meetings');
                dismiss();
              }}
            >
              {copy.takeNotes}
            </button>
          </div>
          <button
            className="ws-text-button self-center"
            onClick={() => {
              void desktopRecordingBridge()
                ?.setMeetingRemindersEnabled?.(false)
                .then(dismiss)
                .catch(() => {});
            }}
          >
            {copy.disable}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
